'use client'

import type { ReactNode, Ref } from 'react'

import Question from '../../icons/Question'
import { Button } from '../_base/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../_base/DropdownMenu'
import { Tooltip, TooltipContent, TooltipTrigger } from '../_base/Tooltip'

type HelpMenuItemBase = {
  id: string
  label: string
  /** Glyph, rendered inside the tinted tile; pass e.g. <BookFilled className="size-3.5" /> */
  icon: ReactNode
  'data-testid'?: string
}

export type HelpMenuExternalLinkItem = HelpMenuItemBase & {
  /** External link — rendered as <a target="_blank" rel="noopener noreferrer"> */
  href: string
  onSelect?: never
}

export type HelpMenuActionItem = HelpMenuItemBase & {
  /** Action item — rendered as a DropdownMenuItem with onSelect. */
  onSelect: () => void
  href?: never
}

export type HelpMenuItem = HelpMenuExternalLinkItem | HelpMenuActionItem

export type HelpMenuProps = {
  items: readonly HelpMenuItem[]
  onOpenChange?: (open: boolean) => void
  /** Fires with the item id whenever an item is chosen (link or action). */
  onItemSelect?: (id: string) => void
  /** Slot rendered below the items (e.g. a "Recent releases" section). */
  footer?: ReactNode
  /** Forwarded to the trigger button (e.g. a dialog's return-focus target). */
  ref?: Ref<HTMLButtonElement>
}

/** Small tinted tile wrapping a menu-leading icon glyph. */
function IconTile({ children }: { children: ReactNode }) {
  return (
    <span className="bg-primary/10 text-info-foreground [&_svg]:!text-info-foreground flex size-6 shrink-0 items-center justify-center rounded-md">
      {children}
    </span>
  )
}

/**
 * "?" support menu for top navs: a tooltip-wrapped secondary icon trigger
 * opening a dropdown of external links and actions.
 */
export function HelpMenu({ items, onOpenChange, onItemSelect, footer, ref }: HelpMenuProps) {
  'use memo'

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              type="button"
              variant="secondary"
              size="iconMd"
              data-testid="help-menu-trigger"
            >
              <span className="sr-only">Help</span>
              <Question className="text-muted-foreground size-5" />
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>Help</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" sideOffset={8} className="text-accent-foreground w-64">
        {items.map(item =>
          item.href !== undefined ? (
            <DropdownMenuItem key={item.id} data-testid={item['data-testid']} asChild>
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onItemSelect?.(item.id)}
                className="gap-2"
              >
                <IconTile>{item.icon}</IconTile>
                {item.label}
              </a>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              key={item.id}
              data-testid={item['data-testid']}
              className="gap-2"
              onSelect={() => {
                onItemSelect?.(item.id)
                item.onSelect()
              }}
            >
              <IconTile>{item.icon}</IconTile>
              {item.label}
            </DropdownMenuItem>
          )
        )}
        {footer}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
